/**
 * ModePreviewButton — tap to hear a 3-second sample of a given mode.
 *
 * Plays a short voice clip at voiceVolume(mode) + ambient music at
 * musicVolume(mode), so the user can actually *feel* the difference
 * between Clear / Soft / Deep before committing.
 *
 * - Only one preview plays at a time across the whole app (others are
 *   auto-stopped via a module-level `activePreview` ref).
 * - Lightweight: reuses the 2-breaths voice clip + soft_piano ambient
 *   that are already shipped with the backend — no extra assets.
 */
import { useEffect, useRef, useState } from "react";
import { Play, Square, Loader2 } from "lucide-react";
import { voiceVolume, musicVolume } from "../../utils/modeAudio";

const API = process.env.REACT_APP_BACKEND_URL;
const PREVIEW_VOICE_URL  = `${API}/api/breathing-audio/2_breaths.mp3`;
const PREVIEW_MUSIC_URL  = `${API}/api/audio/ambient/soft_piano.mp3`;
const PREVIEW_DURATION_MS = 3500;

// Shared across all instances so tapping a new button stops the current one.
let activeInstance = null;

export default function ModePreviewButton({ mode, accent = "#CBA153", size = 26 }) {
  const [state, setState] = useState("idle"); // idle | loading | playing
  const voiceRef = useRef(null);
  const musicRef = useRef(null);
  const stopTimerRef = useRef(null);

  const stopThis = () => {
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    [voiceRef, musicRef].forEach((r) => {
      if (r.current) {
        try { r.current.pause(); } catch (_) {}
        r.current = null;
      }
    });
    if (activeInstance === stopThis) activeInstance = null;
    setState("idle");
  };

  // Clean up on unmount
  useEffect(() => () => stopThis(), []);

  const play = async (e) => {
    // Guard — don't bubble to the parent mode button
    e.stopPropagation();
    e.preventDefault();

    if (state !== "idle") { stopThis(); return; }

    // Stop any other preview currently running
    if (activeInstance && activeInstance !== stopThis) activeInstance();
    activeInstance = stopThis;

    setState("loading");

    try {
      const voice = new Audio(PREVIEW_VOICE_URL);
      const music = new Audio(PREVIEW_MUSIC_URL);
      voiceRef.current = voice;
      musicRef.current = music;
      voice.volume = voiceVolume(mode);
      music.volume = musicVolume(mode);
      music.loop = true;

      // Start both; voice lags ~300ms so the soft-piano bed settles first.
      await music.play().catch(() => {});
      setTimeout(() => {
        if (voiceRef.current === voice) voice.play().catch(() => {});
      }, 300);

      setState("playing");

      // Auto-stop after the window, with a quick fade to avoid hard cut.
      stopTimerRef.current = setTimeout(() => {
        const fade = setInterval(() => {
          let done = true;
          [voiceRef.current, musicRef.current].forEach((el) => {
            if (el && el.volume > 0.02) {
              try { el.volume = Math.max(0, el.volume - 0.05); } catch (_) {}
              done = false;
            }
          });
          if (done) {
            clearInterval(fade);
            stopThis();
          }
        }, 60);
      }, PREVIEW_DURATION_MS);
    } catch (_) {
      stopThis();
    }
  };

  const Icon = state === "loading" ? Loader2 : state === "playing" ? Square : Play;
  const title =
    state === "playing" ? "Stop preview"
    : state === "loading" ? "Loading preview…"
    : `Preview ${mode} mode`;

  // Rendered as a <span role="button"> instead of <button> so it can be
  // safely nested inside a parent <button> (HTML disallows button-in-button
  // and React fires a hydration warning otherwise). Click propagation is
  // stopped inside `play()` so tapping preview never commits the parent.
  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      play(e);
    }
  };

  return (
    <span
      role="button"
      tabIndex={0}
      onClick={play}
      onKeyDown={onKeyDown}
      title={title}
      aria-label={title}
      data-testid={`mode-preview-${mode}`}
      className="inline-flex items-center justify-center rounded-full transition-colors cursor-pointer select-none"
      style={{
        width: size,
        height: size,
        background: state === "playing" ? `${accent}33` : "rgba(255,255,255,0.06)",
        border: `1px solid ${state === "playing" ? accent + "99" : "rgba(255,255,255,0.12)"}`,
        color: state === "playing" ? accent : "rgba(255,255,255,0.7)",
      }}
    >
      <Icon
        size={Math.max(10, size - 14)}
        strokeWidth={1.8}
        className={state === "loading" ? "animate-spin" : ""}
      />
    </span>
  );
}
